// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { GuestWaitingList, LookEntity } from '@seaters/constants';
import { Card, ConfigProvider, Flex, Space, Typography } from 'antd';

import { useAddToCalendar } from './hooks';
import dayjs from 'dayjs';

import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { useTranslation } from 'react-i18next';
import { Button, primaryColor } from '@seaters/ui';
import { getBrandColors, getLocale, getTranslationWithLang } from './utils';
import { CardHeading } from './CardHeading';

dayjs.extend(LocalizedFormat);

const { Text } = Typography;

type EventInformationCardProps = {
  fanGroupData: LookEntity;
  waitingListData: GuestWaitingList;
};

export function EventInformationCard({
  fanGroupData,
  waitingListData,
}: EventInformationCardProps) {
  const { t, i18n } = useTranslation();

  const { addToCalendar } = useAddToCalendar({ waitingListData });

  const locale = getLocale();
  const brandTextColor = getBrandColors(fanGroupData.color, primaryColor).text;
  const city = getTranslationWithLang(waitingListData.venueCity, i18n.language);
  const country = getTranslationWithLang(
    waitingListData.venueCountry,
    i18n.language
  );

  const getDate = (eventDate: string) => {
    const date = dayjs(eventDate).locale(locale).format('HH:mm LL');

    return date;
  };

  const seeInMaps = () => {
    window.open(
      'http://www.google.com/maps/place/' +
        waitingListData.venueLatitude +
        ',' +
        waitingListData.venueLongitude,
      '_blank'
    );
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            headerFontSize: 14,
          },
        },
      }}
    >
      <Card title={<CardHeading>{t('guest_app_info')}</CardHeading>}>
        <Card.Grid hoverable={false} style={{ width: '50%' }}>
          <Flex
            style={{ height: '100%' }}
            vertical
            justify="space-between"
            gap="middle"
          >
            <Space direction="vertical">
              <ConfigProvider
                theme={{
                  token: {
                    colorText: brandTextColor,
                  },
                }}
              >
                <Space style={{ color: brandTextColor }}>
                  <Text>
                    <ClockCircleOutlined rev={undefined} aria-hidden />{' '}
                    {getDate(waitingListData.eventStartDate)}
                  </Text>
                </Space>
              </ConfigProvider>

              {waitingListData.eventEndDate && (
                <Space>
                  <Text type="secondary">
                    <ClockCircleOutlined rev={undefined} aria-hidden />{' '}
                    {getDate(waitingListData.eventEndDate)}
                  </Text>
                </Space>
              )}
            </Space>
            <Button type="primary" onClick={addToCalendar} shape="round">
              {t('guest_app_calendar')}{' '}
              <CalendarOutlined rev={undefined} aria-hidden />
            </Button>
          </Flex>
        </Card.Grid>

        <Card.Grid hoverable={false} style={{ width: '50%' }}>
          <Flex
            style={{ height: '100%' }}
            vertical
            justify="space-between"
            gap="middle"
          >
            <Space direction="vertical">
              <ConfigProvider
                theme={{
                  token: {
                    colorText: brandTextColor,
                  },
                }}
              >
                <Space style={{ color: brandTextColor }}>
                  <Text>
                    <EnvironmentOutlined rev={undefined} aria-hidden />{' '}
                    {waitingListData.translatedVenueName}
                  </Text>
                </Space>
              </ConfigProvider>
              <Text type="secondary">
                <span lang={city.lang}>{city.text}</span>,{' '}
                <span lang={country.lang}>{country.text}</span>
              </Text>
            </Space>
            <Button type="primary" onClick={seeInMaps} shape="round">
              {t('guest_app_maps')}{' '}
              <SearchOutlined rev={undefined} aria-hidden />
            </Button>
          </Flex>
        </Card.Grid>
      </Card>
    </ConfigProvider>
  );
}
